import { Component, Input, OnInit } from '@angular/core';
import { IonicModule, ModalController } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Timestamp } from '@angular/fire/firestore';
import { SongService } from '../../services/song.service';
import { PlaylistService } from '../../services/playlist.service';
import { TranscriptService } from '../../services/transcript.service';
import { LrclibService } from '../../services/lrclib.service';

@Component({
  selector: 'app-submit-song-modal',
  standalone: true,
  imports: [IonicModule, CommonModule, FormsModule, TranslateModule],
  templateUrl: './submit-song-modal.component.html',
  styleUrls: ['./submit-song-modal.component.scss'],
})
export class SubmitSongModalComponent implements OnInit {
  @Input() language = '';
  @Input() topic = '';
  @Input() submittedBy = '';
  @Input() submittedByUid = '';
  @Input() songCount = 0;

  // Edit mode inputs
  @Input() editMode = false;
  @Input() songId = '';
  @Input() initialTitle = '';
  @Input() initialNote = '';
  @Input() initialUrl = '';

  youtubeUrl = '';
  title = '';
  note = '';
  submitting = false;
  errors: { youtubeUrl?: string } = {};

  // Transcript state
  fetchingTranscript = false;
  transcriptStatus: 'idle' | 'success' | 'error' = 'idle';
  transcriptMessage = '';
  transcriptPreview = '';

  private static readonly YOUTUBE_REGEX =
    /^(https?:\/\/)?(www\.|m\.|music\.)?(youtube\.com\/(watch\?.*v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;

  constructor(
    private modalController: ModalController,
    private songService: SongService,
    private playlistService: PlaylistService,
    private transcriptService: TranscriptService,
    private lrclibService: LrclibService
  ) {}

  ngOnInit() {
    if (this.editMode) {
      this.youtubeUrl = this.initialUrl;
      this.note = this.initialNote;
      this.title = this.initialTitle;
    } else if (this.initialTitle) {
      this.title = this.initialTitle;
    }
  }

  validate(): boolean {
    this.errors = {};
    const url = this.youtubeUrl.trim();
    if (!url) {
      this.errors.youtubeUrl = 'Required';
    } else if (!SubmitSongModalComponent.YOUTUBE_REGEX.test(url)) {
      this.errors.youtubeUrl = 'Please enter a valid YouTube URL';
    }
    return Object.keys(this.errors).length === 0;
  }

  private extractVideoId(url: string): string {
    const match = url.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : '';
  }

  private async fetchYouTubeMetadata(url: string): Promise<{ title: string; channelName: string; thumbnailUrl: string }> {
    try {
      const oEmbedUrl = `https://www.youtube.com/oembed?url=${encodeURIComponent(url)}&format=json`;
      const res = await fetch(oEmbedUrl);
      if (res.ok) {
        const data = await res.json();
        return {
          title: data.title || '',
          channelName: data.author_name || '',
          thumbnailUrl: data.thumbnail_url || ''
        };
      }
    } catch { /* network error — fall back to constructed thumbnail */ }
    const videoId = this.extractVideoId(url);
    return {
      title: '',
      channelName: '',
      thumbnailUrl: videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : ''
    };
  }

  async submit() {
    if (!this.validate()) return;

    this.submitting = true;
    try {
      const url = this.youtubeUrl.trim();

      if (this.editMode && this.songId) {
        const updates: Parameters<SongService['updateSong']>[1] = {
          title: this.title.trim(),
          note: this.note.trim(),
          youtubeUrl: url,
        };
        if (url !== this.initialUrl) {
          const { channelName, thumbnailUrl } = await this.fetchYouTubeMetadata(url);
          updates.channelName = channelName || undefined;
          updates.thumbnailUrl = thumbnailUrl || undefined;
        }
        await this.songService.updateSong(this.songId, updates);
        this.modalController.dismiss({ updated: true });
        return;
      }

      if (this.songCount >= 100) {
        this.errors.youtubeUrl = 'This playlist has reached the 100 song limit';
        return;
      }

      const duplicate = await this.songService.isDuplicateUrl(this.language, this.topic, url);
      if (duplicate) {
        this.errors.youtubeUrl = 'This video is already in the playlist';
        return;
      }
      const { title, channelName, thumbnailUrl } = await this.fetchYouTubeMetadata(url);
      const docRef = await this.songService.addSong({
        language: this.language,
        topic: this.topic,
        title,
        channelName: channelName || undefined,
        thumbnailUrl: thumbnailUrl || undefined,
        youtubeUrl: url,
        note: this.note.trim(),
        upvotes: 0,
        downvotes: 0,
        submittedBy: this.submittedBy,
        submittedByUid: this.submittedByUid || undefined,
        createdAt: Timestamp.now()
      });
      await this.playlistService.incrementSongCount(this.topic);

      // Try lrclib first (fast, free, no cloud IP restrictions).
      // Fall back to YouTube captions if lrclib has no synced lyrics.
      this.fetchLyricsInBackground(docRef.id, title, channelName, url);

      this.modalController.dismiss({ submitted: true });
    } catch (error) {
      console.error('Error saving song:', error);
    } finally {
      this.submitting = false;
    }
  }

  /**
   * Tries lrclib for synced lyrics; if none found falls back to YouTube captions.
   * Fire-and-forget — called after the song document is created.
   */
  private async fetchLyricsInBackground(
    songId: string,
    trackName: string,
    artistName: string,
    youtubeUrl: string
  ): Promise<void> {
    try {
      const segments = await this.lrclibService.getSegments(trackName, artistName);
      if (segments?.length) {
        await this.songService.saveSegments(songId, segments);
        return;
      }
    } catch { /* lrclib failed — continue to YouTube captions */ }

    // Fall back to YouTube captions
    this.transcriptService.fetchTranscript(songId, youtubeUrl, this.language).catch(() => {});
  }

  async fetchTranscript() {
    if (!this.songId || !this.youtubeUrl.trim()) return;
    this.fetchingTranscript = true;
    this.transcriptStatus = 'idle';
    this.transcriptMessage = '';
    this.transcriptPreview = '';
    try {
      const result = await this.transcriptService.fetchTranscript(this.songId, this.youtubeUrl.trim());
      this.transcriptStatus = 'success';
      this.transcriptMessage = `Saved ${result.lineCount} caption lines.`;
      this.transcriptPreview = result.transcript.slice(0, 300) + (result.transcript.length > 300 ? '…' : '');
    } catch (err: any) {
      this.transcriptStatus = 'error';
      this.transcriptMessage = err?.message ?? 'Failed to fetch captions.';
    } finally {
      this.fetchingTranscript = false;
    }
  }

  dismiss() {
    this.modalController.dismiss();
  }
}
